// =============================================================================
// SEVRIC Battery Care — per-user dashboard preferences.
//
// Each user's Today page is theirs. Layout (which widgets, where, what size,
// what config) is persisted to /users/{uid}/preferences/batteryDashboard so
// it follows them on every device they sign in from.
//
// First sign-in shows a sensible starter template based on the user's role
// hint. From the next interaction it's their canvas — drag, resize, hide,
// add, swap. The starter is a STARTING POINT, not a fixed layout.
// =============================================================================
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase.js";

export const DASHBOARD_PREFS_VERSION = 1;

// -----------------------------------------------------------------------------
// Starter templates by role hint.
// -----------------------------------------------------------------------------
const STARTER_TEMPLATES = {
  admin: {
    widgets: [
      { i: "ai-surfaces",        x: 0, y: 0,  w: 8,  h: 5, config: {} },
      { i: "weather",            x: 8, y: 0,  w: 4,  h: 5, config: {} },
      { i: "at-risk",            x: 0, y: 5,  w: 8,  h: 8, config: {} },
      { i: "recent-checks",      x: 8, y: 5,  w: 4,  h: 8, config: {} },
      { i: "historian-preview",  x: 0, y: 13, w: 6,  h: 7, config: {} },
      { i: "messages",           x: 6, y: 13, w: 6,  h: 7, config: {} },
    ],
  },
  manager: {
    widgets: [
      { i: "ai-surfaces",   x: 0, y: 0,  w: 8,  h: 5, config: {} },
      { i: "weather",       x: 8, y: 0,  w: 4,  h: 5, config: {} },
      { i: "at-risk",       x: 0, y: 5,  w: 8,  h: 8, config: {} },
      { i: "recent-checks", x: 8, y: 5,  w: 4,  h: 8, config: {} },
    ],
  },
  // Default — the porter / tech who walks the lot every morning.
  // Weather is critical on the porter view because cold mornings change
  // voltage decisions; it sits top-right where they glance first.
  default: {
    widgets: [
      { i: "ai-surfaces",   x: 0, y: 0,  w: 8,  h: 5, config: { audience: "porter" } },
      { i: "weather",       x: 8, y: 0,  w: 4,  h: 5, config: {} },
      { i: "at-risk",       x: 0, y: 5,  w: 12, h: 8, config: {} },
      { i: "recent-checks", x: 0, y: 13, w: 12, h: 6, config: {} },
    ],
  },
};

export function starterTemplateFor(role) {
  return STARTER_TEMPLATES[role] || STARTER_TEMPLATES.default;
}

// -----------------------------------------------------------------------------
// Load + save + reset
// -----------------------------------------------------------------------------
export async function loadDashboardPrefs(uid, role) {
  if (!uid) return { ...starterTemplateFor(role), source: "starter-default" };
  try {
    const ref = doc(db, "users", uid, "preferences", "batteryDashboard");
    const snap = await getDoc(ref);
    if (!snap.exists()) return { ...starterTemplateFor(role), source: "starter-first-load" };
    const data = snap.data() || {};
    if (!Array.isArray(data.widgets) || data.widgets.length === 0) {
      return { ...starterTemplateFor(role), source: "starter-empty" };
    }
    return { widgets: data.widgets, source: "saved", v: data.v };
  } catch (err) {
    console.warn("[battery dashboardPrefs] load failed, using starter:", err?.message);
    return { ...starterTemplateFor(role), source: "starter-error" };
  }
}

export async function saveDashboardPrefs(uid, widgets) {
  if (!uid) return;
  try {
    const ref = doc(db, "users", uid, "preferences", "batteryDashboard");
    await setDoc(ref, {
      widgets,
      v: DASHBOARD_PREFS_VERSION,
      editedAt: serverTimestamp(),
    }, { merge: true });
  } catch (err) {
    console.warn("[battery dashboardPrefs] save failed:", err?.message);
  }
}

export async function resetDashboardPrefs(uid) {
  if (!uid) return;
  try {
    const ref = doc(db, "users", uid, "preferences", "batteryDashboard");
    await setDoc(ref, {
      widgets: [],
      v: DASHBOARD_PREFS_VERSION,
      editedAt: serverTimestamp(),
    }, { merge: true });
  } catch (err) {
    console.warn("[battery dashboardPrefs] reset failed:", err?.message);
  }
}
